import useResizeObserver from '@react-hook/resize-observer';
import { useGesture } from '@use-gesture/react';
import { getCurrentRef, useDoubleClick, useEventListener, usePreventDefaultMultiTouch, usePreventNextClick } from '@vista-digital/react-tools';
import { animate, useMotionValueEvent } from 'framer-motion';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { getKeyCodeForEvent, KeyCode } from '../../../utils/key-code';
import { ZoomControllerButtonType as ButtonType } from '../types/button-type';
import { getElementCentrePoint } from '../utils/get-element-centre-point';
import { getElementDimensions } from '../utils/get-element-dimensions';
import { getElementRelativePoint } from '../utils/get-element-relative-point';
import { getMinContentScale } from '../utils/get-min-content-scale';
import { getScaledScrollPosition } from '../utils/get-scaled-scroll-position';
import { useScaleControls } from './use-scale-controls';
import { useScrollControls } from './use-scroll-controls';
const transition = {
    type: 'tween',
    duration: 0.4,
    ease: [0.25, 1, 0.5, 1]
};
// const defaultZoomButtonPadding = 44;
const maxScale = 1.25;
const scaleIncrement = 1.5;
const scrollIncrement = 0.25;
const minimumDragExtent = 1; // The minimum movement to be considered a drag (not a click)
export function useZoomHandler(options) {
    const { isDisabled, onScrollPositionChange } = options;
    // React refs to the root container, viewport container, and content container.
    const containerRef = useRef(null);
    const viewportRef = useRef(null);
    const contentRef = useRef(null);
    // The current origins used for scaling the content.
    const scalingOrigin = useRef(undefined);
    const unitContentOrigin = useRef(undefined);
    // Wrap `props.contentDimensions` with useMemo to ensure that hooks are only re-run when the dimensions change.
    const contentDimensions = useMemo(() => ({ width: options.contentDimensions.width, height: options.contentDimensions.height }), [options.contentDimensions.width, options.contentDimensions.height]);
    // Controls used to get and set the content scale.
    const scaleControls = useScaleControls({
        initial: 1,
        min: 1,
        max: maxScale,
        contentDimensions,
        contentRef
    });
    // Controls used to get and set the viewport scroll position.
    const scrollControls = useScrollControls({
        containerRef: viewportRef,
        transition,
        scrollIncrement
    });
    const getViewportElement = () => {
        return getCurrentRef(viewportRef);
    };
    const getViewportCenterPoint = () => {
        const viewport = getViewportElement();
        return getElementCentrePoint(viewport);
    };
    // Returns the unit coords of the content currently at the specified point in the viewport
    const getCurrentUnitContentCoords = (viewpointCoords) => {
        const scrollPosition = scrollControls.scrollPosition.get();
        const scale = scaleControls.scale.get();
        const scaledContentWidth = contentDimensions.width * scale;
        const scaledContentHeight = contentDimensions.height * scale;
        const viewport = getViewportElement();
        const viewportDimensions = getElementDimensions(viewport, 'content-box');
        // Return the centre if no scroll bars
        const x = scaledContentWidth > viewportDimensions.width
            ? (scrollPosition.x + viewpointCoords.x) / scaledContentWidth
            : (viewpointCoords.x - (viewportDimensions.width - scaledContentWidth) / 2) / scaledContentWidth;
        const y = scaledContentHeight > viewportDimensions.height
            ? (scrollPosition.y + viewpointCoords.y) / scaledContentHeight
            : (viewpointCoords.y - (viewportDimensions.height - scaledContentHeight) / 2) / scaledContentHeight;
        return { x, y };
    };
    const setScalingOrigin = (viewpointCoords) => (scalingOrigin.current = viewpointCoords);
    const setUnitContentOrigin = (viewpointCoords) => (unitContentOrigin.current = getCurrentUnitContentCoords(viewpointCoords));
    const setCurrentUnitContentOrigin = () => {
        const viewport = getViewportElement();
        const viewportDimensions = getElementDimensions(viewport, 'content-box');
        unitContentOrigin.current = getCurrentUnitContentCoords({
            x: viewportDimensions.width / 2,
            y: viewportDimensions.height / 2
        });
    };
    const unsetScalingOrigins = () => {
        scalingOrigin.current = undefined;
        unitContentOrigin.current = undefined;
    };
    const getScalingOrigin = () => {
        var _a;
        return (_a = scalingOrigin.current) !== null && _a !== void 0 ? _a : getViewportCenterPoint();
    };
    const getUnitContentOrigin = () => {
        var _a;
        return (_a = unitContentOrigin.current) !== null && _a !== void 0 ? _a : { x: 0.5, y: 0.5 };
    };
    // Call the provided `onScrollPositionChange` method when the scroll position changes.
    useMotionValueEvent(scrollControls.scrollPosition, 'change', newScrollPosition => onScrollPositionChange === null || onScrollPositionChange === void 0 ? void 0 : onScrollPositionChange(newScrollPosition));
    // The content should initially be shown at the minimum zoom level.
    // Use `useLayoutEffect` instead of `useEffect` to ensure that the content is correctly displayed on first render.
    useLayoutEffect(() => {
        const minScale = getMinContentScale(viewportRef, contentDimensions);
        scaleControls.setMinScale(minScale);
        unsetScalingOrigins();
        setScale(minScale, true, false);
    }, [contentDimensions]);
    // Update the minScale when the viewport is resized.
    useResizeObserver(viewportRef, () => {
        const currentScale = scaleControls.scale.get();
        const currentMinScale = scaleControls.minScale.get();
        const newMinScale = getMinContentScale(viewportRef, contentDimensions);
        scaleControls.setMinScale(newMinScale);
        unsetScalingOrigins();
        const isZoomedIn = currentScale > currentMinScale;
        // Update the current scale if the user hasn't zoomed in past the minimum (default) zoom level.
        if (!isZoomedIn) {
            setScale(newMinScale, true, false);
        }
    });
    const updateViewportClass = (scale, minScale) => {
        const zoomedInClass = 'v-zoom-controller-viewport--is-zoomed-in';
        const viewport = getViewportElement();
        if (scale > minScale) {
            viewport.classList.add(zoomedInClass);
        }
        else {
            viewport.classList.remove(zoomedInClass);
        }
    };
    const updateScrollPositions = (scale) => {
        const origin = getScalingOrigin();
        const contentOrigin = getUnitContentOrigin();
        const newScrollX = getScaledScrollPosition(origin.x, contentDimensions.width * scale, contentOrigin.x);
        const newScrollY = getScaledScrollPosition(origin.y, contentDimensions.height * scale, contentOrigin.y);
        scrollControls.setScrollX(newScrollX, false);
        scrollControls.setScrollY(newScrollY, false);
    };
    const updateScaleAndScrollPositions = (newScale, minScale) => {
        const displayedScale = scaleControls.setScale(newScale);
        updateScrollPositions(displayedScale);
        updateViewportClass(displayedScale, minScale);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const setScale = (scale, constrain, animated, onAnimationFinished) => {
        const minScale = scaleControls.minScale.get();
        const currentScale = scaleControls.scale.get();
        let newScale = constrain ? Math.max(Math.min(scale, maxScale), minScale) : scale;
        if (animated) {
            // Prevent a small scale increment from remaining
            if (newScale > minScale && newScale < minScale * Math.sqrt(scaleIncrement)) {
                newScale = minScale;
            }
            else if (newScale < maxScale && newScale > maxScale / Math.sqrt(scaleIncrement)) {
                newScale = maxScale;
            }
        }
        if (animated) {
            animate(currentScale, newScale, Object.assign(Object.assign({}, transition), { onUpdate: latestScale => updateScaleAndScrollPositions(latestScale, minScale) })).then(() => {
                updateScaleAndScrollPositions(newScale, minScale); // Account for slight difference after animate()
                onAnimationFinished === null || onAnimationFinished === void 0 ? void 0 : onAnimationFinished();
            });
        }
        else {
            updateScaleAndScrollPositions(newScale, minScale);
        }
    };
    const constrainScale = () => {
        const minScale = scaleControls.minScale.get();
        const currentScale = scaleControls.scale.get();
        const constrainedScale = currentScale < minScale ? minScale : currentScale > maxScale ? maxScale : undefined;
        if (constrainedScale) {
            setScale(constrainedScale, true, true, () => unsetScalingOrigins());
        }
        else {
            unsetScalingOrigins();
        }
    };
    // Prevent default multi-touch gestures.
    // Workaround for Webkit bug in Safari: https://bugs.webkit.org/show_bug.cgi?id=238543
    usePreventDefaultMultiTouch(viewportRef);
    const { preventNextClick } = usePreventNextClick(viewportRef);
    // Register the gesture handlers for pinch and drag.
    useGesture({
        onPinchStart: ({ origin: [originX, originY] }) => {
            const pinchScalingOrigin = getElementRelativePoint(viewportRef, { x: originX, y: originY });
            setUnitContentOrigin(pinchScalingOrigin);
        },
        onPinch: ({ offset: [pinchScale], delta: [pinchDelta], origin: [originX, originY], event }) => {
            event.preventDefault();
            const pinchScalingOrigin = getElementRelativePoint(viewportRef, { x: originX, y: originY });
            setScalingOrigin(pinchScalingOrigin);
            const previousPinchScale = pinchScale - pinchDelta;
            const newPinchScale = pinchScale / previousPinchScale;
            const newScale = scaleControls.scale.get() * newPinchScale;
            setScale(newScale, false, false);
        },
        onPinchEnd: () => {
            constrainScale();
        },
        onDrag: ({ delta: [deltaX, deltaY], event }) => {
            // Prevent the drag event from propagating so it doesn't inadvertently scroll the container if the cursor leaves the viewport.
            event.preventDefault();
            event.stopPropagation();
            if (deltaX !== 0) {
                scrollControls.scrollXBy(-deltaX, false, false);
            }
            if (deltaY !== 0) {
                scrollControls.scrollYBy(-deltaY, false, false);
            }
        },
        onDragEnd: ({ movement: [movementX, movementY], event }) => {
            // Dragging with a mouse also produces a click event which we should prevent from being passed on.
            if (event instanceof PointerEvent && event.pointerType === 'mouse') {
                // Clicking without dragging also calls onDrag, onDragEnd so...
                const dragExtent = Math.max(Math.abs(movementX), Math.abs(movementY));
                if (dragExtent >= minimumDragExtent) {
                    // ... only prevent the click if there really was a drag gesture.
                    preventNextClick();
                }
            }
            scrollControls.resetContentOffset();
        }
    }, {
        target: viewportRef,
        eventOptions: { passive: false },
        enabled: !isDisabled
    });
    // Zoom in/out when the content is double-clicked.
    const handleDoubleClick = useCallback((event) => {
        if (isDisabled) {
            return;
        }
        const currentScale = scaleControls.scale.get();
        const minScale = scaleControls.minScale.get();
        if (currentScale === minScale) {
            const clickedCoords = getElementRelativePoint(viewportRef, { x: event.clientX, y: event.clientY });
            setScalingOrigin(clickedCoords);
            setUnitContentOrigin(clickedCoords);
            setScale(1, true, true, () => unsetScalingOrigins());
        }
        else {
            setScale(minScale, true, true, () => unsetScalingOrigins());
        }
        // Prevent the original event from being refired.
        return false;
    }, [viewportRef, scaleControls, isDisabled, setUnitContentOrigin, setScale]);
    const handleSingleClick = useCallback((event) => {
        if (isDisabled) {
            return;
        }
        if (!(event instanceof PointerEvent) || event.pointerType !== 'touch') {
            return;
        }
        const currentScale = scaleControls.scale.get();
        const minScale = scaleControls.minScale.get();
        // Zoom in to the touched area of the content if fully zoomed out and the current scale is less than 75%.
        if (currentScale === minScale && currentScale <= 0.75) {
            const clickedCoords = getElementRelativePoint(viewportRef, { x: event.clientX, y: event.clientY });
            setScalingOrigin(clickedCoords);
            setUnitContentOrigin(clickedCoords);
            setScale(1, true, true, () => unsetScalingOrigins());
            // Prevent the original event from being refired.
            return false;
        }
    }, [viewportRef, scaleControls, isDisabled, setUnitContentOrigin, setScale]);
    // Register the double-click and single-click gesture handlers.
    useDoubleClick({
        targetRef: contentRef,
        onDoubleClick: handleDoubleClick,
        onSingleClick: handleSingleClick
    });
    // Zooms in by one scale increment.
    const zoomIn = useCallback(() => {
        setCurrentUnitContentOrigin();
        return setScale(scaleControls.scale.get() * scaleIncrement, true, true);
    }, [setScale, scaleControls]);
    // Zooms out by one scale increment.
    const zoomOut = useCallback(() => {
        setCurrentUnitContentOrigin();
        return setScale(scaleControls.scale.get() * (1 / scaleIncrement), true, true);
    }, [setScale, scaleControls]);
    // Resets the zoom to the default (minimum) scale.
    const resetZoom = useCallback(() => {
        setCurrentUnitContentOrigin();
        return setScale(scaleControls.minScale.get(), true, true);
    }, [setScale, scaleControls]);
    // Handle zoom/pan button clicks.
    const handleButtonClick = useCallback((type) => {
        switch (type) {
            case ButtonType.ZoomIn: {
                return zoomIn();
            }
            case ButtonType.ZoomOut: {
                return zoomOut();
            }
            case ButtonType.ResetZoom: {
                return resetZoom();
            }
            case ButtonType.PanUp: {
                return scrollControls.scrollUp(true);
            }
            case ButtonType.PanDown: {
                return scrollControls.scrollDown(true);
            }
            case ButtonType.PanLeft: {
                return scrollControls.scrollLeft(true);
            }
            case ButtonType.PanRight: {
                return scrollControls.scrollRight(true);
            }
        }
    }, [zoomIn, zoomOut, resetZoom, scrollControls]);
    // Handle keyboard key presses.
    const handleKeyDown = useCallback((event) => {
        const keyCode = getKeyCodeForEvent(event);
        switch (keyCode) {
            case KeyCode.Plus: {
                return zoomIn();
            }
            case KeyCode.Minus: {
                return zoomOut();
            }
            case KeyCode.UpArrow: {
                return scrollControls.scrollUp(true);
            }
            case KeyCode.DownArrow: {
                return scrollControls.scrollDown(true);
            }
            case KeyCode.LeftArrow: {
                return scrollControls.scrollLeft(true);
            }
            case KeyCode.RightArrow: {
                return scrollControls.scrollRight(true);
            }
            default: {
                return undefined;
            }
        }
    }, [zoomIn, zoomOut, scrollControls]);
    // Register the keyboard event listener.
    useEventListener(containerRef, 'keydown', handleKeyDown);
    return {
        containerRef,
        viewportRef,
        contentRef,
        handleButtonClick,
        scale: scaleControls.scale,
        minScale: scaleControls.minScale,
        maxScale: scaleControls.maxScale,
        scrollPosition: scrollControls.scrollPosition,
        contentStyles: scrollControls.contentStyles
    };
}
//# sourceMappingURL=use-zoom-handler.js.map